import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Building2, ArrowLeft, Users, TrendingUp, Calendar, Activity,
  Mail, Phone, MapPin, User, Loader2, ChevronRight, Clock,
  CheckSquare, Square, PhoneCall, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/services/api";

const TABS = [
  { id: "overview",      label: "Overview",      icon: Building2 },
  { id: "contacts",      label: "Contacts",      icon: Users },
  { id: "opportunities", label: "Opportunities", icon: TrendingUp },
  { id: "events",        label: "Events",        icon: Calendar },
  { id: "activity",      label: "Activity",      icon: Activity },
];

export default function CRMAccountDetail() {
  const { accountId } = useParams();
  const navigate = useNavigate();

  const [account,       setAccount]       = useState(null);
  const [contacts,      setContacts]      = useState([]);
  const [opportunities, setOpps]          = useState([]);
  const [events,        setEvents]        = useState([]);
  const [activities,    setActivities]    = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [tab,           setTab]           = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, cntRes, oppRes, evtRes] = await Promise.all([
        api.get(`/crm/accounts/${accountId}`),
        api.get("/crm/contacts", { params: { account_id: accountId, limit: 50 } }),
        api.get("/crm/opportunities", { params: { limit: 50 } }),
        api.get("/events", { params: { customer_id: accountId, limit: 20 } }).catch(() => ({ data: [] })),
      ]);
      setAccount(accRes.data);
      setContacts((cntRes.data.results || cntRes.data.contacts || accRes.data.contacts || []));
      setOpps((oppRes.data.results || []).filter(o => o.account_id === accountId));
      setEvents(Array.isArray(evtRes.data) ? evtRes.data : (evtRes.data?.results || []));
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  const loadActivities = useCallback(async () => {
    try {
      const res = await api.get("/crm/activities");
      // Filter to activities related to this account's leads
      setActivities(res.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "activity") loadActivities(); }, [tab, loadActivities]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-[#0176D3]" />
    </div>
  );

  if (!account) return (
    <div className="p-6">
      <p className="text-[#706E6B]">Account not found.</p>
      <button onClick={() => navigate("/crm/accounts")} className="text-[#0176D3] text-sm mt-2">
        ← Back to Accounts
      </button>
    </div>
  );

  const name = account.name || account.customer_name || "—";

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-[#E5E5E5] px-6 py-4 flex-shrink-0">
        <button
          onClick={() => navigate("/crm/accounts")}
          className="flex items-center gap-1.5 text-xs text-[#706E6B] hover:text-[#444444] mb-3 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Accounts
        </button>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0176D3]/20 to-[#014486]/20 border border-[#E5E5E5] flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4.5 h-4.5 text-[#0176D3]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#181818] font-sans">{name}</h1>
              <div className="flex items-center gap-3 text-xs text-[#706E6B]">
                {account.account_number && (
                  <span className="font-mono">{account.account_number}</span>
                )}
                {account.customer_type && (
                  <span className="bg-[#F3F3F3] border border-[#E5E5E5] px-1.5 py-0.5 rounded text-[10px]">
                    {account.customer_type}
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Quick stats */}
          <div className="flex items-center gap-4 text-center hidden sm:flex">
            {[
              { n: contacts.length,      l: "Contacts" },
              { n: opportunities.length, l: "Opportunities" },
              { n: events.length,        l: "Events" },
            ].map(s => (
              <div key={s.l}>
                <p className="text-lg font-bold text-[#181818] font-sans">{s.n}</p>
                <p className="text-[10px] text-[#706E6B] uppercase tracking-wider">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex border-b border-[#E5E5E5] px-6 flex-shrink-0 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`account-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id
                  ? "border-[#0176D3] text-[#0176D3] font-medium"
                  : "border-transparent text-[#706E6B] hover:text-[#444444]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        {/* OVERVIEW */}
        {tab === "overview" && (
          <div className="space-y-5 max-w-4xl">
            {/* Key Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <InfoCard label="Account Name" value={name} icon={Building2} />
              <InfoCard label="Type" value={account.customer_type || "—"} icon={User} />
              <InfoCard label="Status" value={account.status || "ACTIVE"} icon={CheckSquare} />
              <InfoCard label="Email" value={account.email || "—"} icon={Mail} href={account.email ? `mailto:${account.email}` : null} />
              <InfoCard label="Phone" value={account.phone || "—"} icon={Phone} />
              <InfoCard label="Location" value={account.location || account.city || "—"} icon={MapPin} />
            </div>

            {/* Summary Panels */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <SummaryPanel
                icon={TrendingUp}
                label="Open Opportunities"
                value={opportunities.filter(o => !["CLOSED_WON","CLOSED_LOST"].includes(o.stage)).length}
                color="text-[#0176D3]"
              />
              <SummaryPanel
                icon={Calendar}
                label="Active Events"
                value={events.filter(e => e.status !== "CANCELLED").length}
                color="text-[#0176D3]"
              />
              <SummaryPanel
                icon={Users}
                label="Contacts"
                value={contacts.length}
                color="text-[#2E844A]"
              />
            </div>

            {/* Recent activity preview */}
            {contacts.length > 0 && (
              <div>
                <h4 className="text-xs font-mono text-[#706E6B] uppercase tracking-wider mb-3">Primary Contact</h4>
                <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-[#E5E5E5] flex items-center justify-center">
                    <span className="text-xs font-bold text-[#2E844A]">{(contacts[0].name || "?").charAt(0)}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-[#181818] font-medium">{contacts[0].name}</p>
                    <p className="text-xs text-[#706E6B]">{contacts[0].email || contacts[0].phone || "—"}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CONTACTS */}
        {tab === "contacts" && (
          <div className="max-w-4xl">
            {contacts.length === 0 ? (
              <EmptyState icon={Users} title="No Contacts" note="Contacts are created when a Lead is converted via Start Event Planning." />
            ) : (
              <div className="space-y-2">
                {contacts.map(c => (
                  <div
                    key={c.id}
                    data-testid="account-contact-row"
                    className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4 flex items-center gap-3 hover:border-[#0176D3]/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/crm/contacts/${c.id}`)}
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-[#E5E5E5] flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-[#2E844A]">{(c.name || "?").charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#181818] font-medium">{c.name}</p>
                      <p className="text-xs text-[#706E6B]">{c.email || c.phone || "—"}</p>
                    </div>
                    {c.preferred_channel && (
                      <span className="text-[10px] font-mono text-[#706E6B] hidden sm:block">{c.preferred_channel}</span>
                    )}
                    <ChevronRight className="w-4 h-4 text-[#706E6B] flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* OPPORTUNITIES */}
        {tab === "opportunities" && (
          <div className="max-w-4xl">
            {opportunities.length === 0 ? (
              <EmptyState icon={TrendingUp} title="No Opportunities" note="Opportunities are created when a Lead is converted via Start Event Planning." />
            ) : (
              <div className="space-y-2">
                {opportunities.map(o => (
                  <div
                    key={o.id}
                    data-testid="account-opp-row"
                    className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4 flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#181818] font-medium">{o.name || o.opportunity_number}</p>
                      <p className="text-xs text-[#706E6B]">{o.event_type || ""} {o.budget_range ? `· ${o.budget_range}` : ""}</p>
                    </div>
                    <span className="text-[10px] font-mono text-[#706E6B] border border-[#E5E5E5] px-2 py-0.5 rounded">{o.stage}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* EVENTS */}
        {tab === "events" && (
          <div className="max-w-4xl">
            {events.length === 0 ? (
              <EmptyState icon={Calendar} title="No Events" note="Events appear here after Start Event Planning is used for a linked Lead." />
            ) : (
              <div className="space-y-2">
                {events.map(e => (
                  <div
                    key={e.id || e._id}
                    data-testid="account-event-row"
                    onClick={() => navigate(`/imagine/events/${e.id || e._id}`)}
                    className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:border-[#0176D3]/30 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="text-sm text-[#181818] font-medium">{e.name || e.event_number}</p>
                      <p className="text-xs text-[#706E6B]">{e.event_number} {e.event_date ? `· ${fmtDate(e.event_date)}` : ""}</p>
                    </div>
                    <span className="text-[10px] font-mono text-[#706E6B]">{e.planning_stage || e.status}</span>
                    <ChevronRight className="w-4 h-4 text-[#706E6B]" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ACTIVITY */}
        {tab === "activity" && (
          <div className="max-w-2xl">
            {!activities ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-[#0176D3]" />
              </div>
            ) : (
              <div className="space-y-6">
                <ActivityGroup title="Overdue"  items={activities.overdue  || []} variant="overdue" />
                <ActivityGroup title="Upcoming" items={activities.upcoming || []} variant="normal" />
                <ActivityGroup title="Recent"   items={activities.recent   || []} variant="recent" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shared Primitives ─────────────────────────────────────────────────────────

function InfoCard({ label, value, icon: Icon, href }) {
  const content = (
    <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#706E6B]" />
        <p className="text-[10px] text-[#706E6B] uppercase tracking-wider">{label}</p>
      </div>
      <p className={`text-sm font-medium ${href ? "text-[#0176D3] hover:underline" : "text-[#181818]"}`}>{value}</p>
    </div>
  );
  return href ? <a href={href}>{content}</a> : content;
}

function SummaryPanel({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4 text-center">
      <Icon className={`w-5 h-5 mx-auto mb-2 ${color}`} />
      <p className={`text-2xl font-bold font-sans ${color}`}>{value}</p>
      <p className="text-[10px] text-[#706E6B] uppercase tracking-wider mt-1">{label}</p>
    </div>
  );
}

function EmptyState({ icon: Icon, title, note }) {
  return (
    <div className="py-16 text-center">
      <Icon className="w-8 h-8 text-[#444444] mx-auto mb-3" />
      <p className="text-sm text-[#706E6B] font-medium">{title}</p>
      {note && <p className="text-xs text-[#706E6B] mt-1 max-w-sm mx-auto">{note}</p>}
    </div>
  );
}

function ActivityGroup({ title, items, variant }) {
  if (!items.length) return null;
  const color = variant === "overdue" ? "text-rose-400" : "text-[#706E6B]";
  return (
    <div>
      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${color}`}>
        {title} <span className="text-[#706E6B] font-normal normal-case">({items.length})</span>
      </h4>
      <div className="space-y-1.5">
        {items.map((item, i) => {
          const isComplete = item.status === "COMPLETE" || item.status === "COMPLETED";
          const isOverdue  = variant === "overdue";
          return (
            <div key={item.id || i}
              className={`flex items-start gap-2.5 py-2.5 px-3.5 rounded-lg bg-white border ${isOverdue ? "border-rose-500/20" : "border-[#E5E5E5]"}`}>
              {item._type === "FOLLOW_UP"
                ? <Clock className={`w-3.5 h-3.5 mt-0.5 ${isOverdue ? "text-rose-400" : "text-[#8C4B00]"}`} />
                : item.task_type === "CALL_LOG"
                ? <PhoneCall className="w-3.5 h-3.5 mt-0.5 text-[#706E6B]" />
                : isComplete
                ? <CheckSquare className="w-3.5 h-3.5 mt-0.5 text-[#2E844A]" />
                : <Square className="w-3.5 h-3.5 mt-0.5 text-[#706E6B]" />
              }
              <div className="flex-1 min-w-0">
                <p className={`text-xs ${isComplete ? "line-through text-[#706E6B]" : "text-[#444444]"}`}>
                  {item.title || item.reason || item.follow_up_type || item._type}
                </p>
                <p className="text-[10px] text-[#706E6B] mt-0.5">
                  {fmtDate(item.due_date || item.scheduled_at || item.created_at)}
                </p>
              </div>
              {item.status && (
                <span className={`text-[10px] font-mono ${
                  isComplete ? "text-[#2E844A]" : isOverdue ? "text-rose-400" : "text-[#706E6B]"
                }`}>{item.status}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" }); }
  catch { return "—"; }
}
